import React from 'react';
import { Box, Chip } from '@mui/material';
import {
  ViewList as AllIcon,
  Description as SoapIcon,
  Medication as MedIcon,
  Vaccines as VaxIcon,
  Healing as ProcIcon,
  Note as VisitIcon,
  Science as LabIcon,
  Phone as CommIcon,
  PriorityHigh as ProblemIcon,
  CalendarToday as RecallIcon,
  LocalHospital as DischargeIcon,
  Receipt as InvoiceIcon,
  Assignment as TreatmentSheetIcon,
  FolderOpen as FilesIcon,
} from '@mui/icons-material';
import { TimelineKind } from './TimelineRow';

export type ActiveFilter = TimelineKind | 'all' | 'problem' | 'recall' | 'discharge' | 'invoice' | 'treatment_sheet' | 'files';

interface ChipDef {
  key: ActiveFilter;
  label: string;
  icon: React.ReactElement;
  color: string;
}

const CHIPS: ChipDef[] = [
  { key: 'all', label: 'All', icon: <AllIcon sx={{ fontSize: 14 }} />, color: '#3B2E1A' },
  { key: 'soap', label: 'SOAP', icon: <SoapIcon sx={{ fontSize: 14 }} />, color: '#1565C0' },
  { key: 'medication', label: 'Rx', icon: <MedIcon sx={{ fontSize: 14 }} />, color: '#2E7D32' },
  { key: 'vaccination', label: 'Vax', icon: <VaxIcon sx={{ fontSize: 14 }} />, color: '#6A1B9A' },
  { key: 'procedure', label: 'Procedures', icon: <ProcIcon sx={{ fontSize: 14 }} />, color: '#C62828' },
  { key: 'visit_note', label: 'Visit Notes', icon: <VisitIcon sx={{ fontSize: 14 }} />, color: '#455A64' },
  { key: 'lab', label: 'Labs & Imaging', icon: <LabIcon sx={{ fontSize: 14 }} />, color: '#EF6C00' },
  { key: 'discharge', label: 'Discharges', icon: <DischargeIcon sx={{ fontSize: 14 }} />, color: '#4527A0' },
  { key: 'treatment_sheet', label: 'Treatment Sheets', icon: <TreatmentSheetIcon sx={{ fontSize: 14 }} />, color: '#00695C' },
  { key: 'invoice', label: 'Invoices', icon: <InvoiceIcon sx={{ fontSize: 14 }} />, color: '#1565C0' },
  { key: 'communication', label: 'Client Comms', icon: <CommIcon sx={{ fontSize: 14 }} />, color: '#00838F' },
  { key: 'problem', label: 'Problem List', icon: <ProblemIcon sx={{ fontSize: 14 }} />, color: '#D84315' },
  { key: 'recall', label: 'Recalls', icon: <RecallIcon sx={{ fontSize: 14 }} />, color: '#5E35B1' },
  { key: 'files', label: 'Files', icon: <FilesIcon sx={{ fontSize: 14 }} />, color: '#8B4513' },
];

export interface FilterChipsProps {
  active: ActiveFilter;
  counts: Record<string, number>;
  onSelect: (kind: ActiveFilter) => void;
}

/**
 * Familiar pill-style chip filter — the type icon is retained as a small
 * accent on the left, but the chip shape is the standard rounded pill
 * users already know from the rest of the app. Active chip fills with the
 * type's brand color so identity isn't lost, but the row no longer screams
 * for attention the way the big tiles did.
 */
const FilterChips: React.FC<FilterChipsProps> = ({ active, counts, onSelect }) => {
  return (
    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
      {CHIPS.map((chip) => {
        const isActive = active === chip.key;
        const count = counts[chip.key] ?? 0;
        return (
          <Chip
            key={chip.key}
            icon={React.cloneElement(chip.icon, {
              sx: { ...chip.icon.props.sx, color: isActive ? '#fff !important' : chip.color },
            })}
            label={`${chip.label} ${count}`}
            size="small"
            variant={isActive ? 'filled' : 'outlined'}
            onClick={() => onSelect(chip.key)}
            data-testid={`filter-chip-${chip.key}`}
            sx={{
              height: 28,
              fontSize: '0.78rem',
              fontWeight: isActive ? 700 : 500,
              cursor: 'pointer',
              borderColor: isActive ? chip.color : 'rgba(139,94,52,0.2)',
              bgcolor: isActive ? chip.color : 'transparent',
              color: isActive ? '#fff' : 'text.primary',
              transition: 'all 140ms cubic-bezier(0.4, 0, 0.2, 1)',
              '& .MuiChip-icon': { ml: 0.75 },
              '& .MuiChip-label': { px: 1, fontVariantNumeric: 'tabular-nums' },
              '&:hover': {
                bgcolor: isActive ? chip.color : `${chip.color}10`,
                borderColor: chip.color,
              },
            }}
          />
        );
      })}
    </Box>
  );
};

export default FilterChips;
